import { useMemo, useState } from "react";
import { Stack } from "expo-router";
import { Tag } from "@/shared/components/ui/tag";
import {
  Platform,
  Pressable,
  ScrollView,
  Share,
  Text,
  View,
} from "react-native";
import { Image } from "expo-image";
import { Ionicons } from "@expo/vector-icons";
import * as Clipboard from "expo-clipboard";
import { resolveStorageUrl } from "@lezz/supabase";
import type { Event, EventParticipantDetails } from "@lezz/shared";
import { Avatar } from "@/shared/components/ui/avatar";
import { BackButton } from "@/shared/components/ui/back-button";
import { Button } from "@/shared/components/ui/button";
import { Heading } from "@/shared/components/ui/heading";
import { IconButton } from "@/shared/components/ui/icon-button";
import { Pill } from "@/shared/components/ui/pill";
import { cn, safeOpenURL } from "@/shared/lib/utils";
import { EventMap } from "@/features/events/components/event-map";
import { EventCouponCard } from "@/features/events/components/event-coupon-card";
import { getCouponViewerState } from "@/features/events/lib/coupon";
import type { CouponRevealState } from "@/features/events/lib/coupon";
import { useCSSVariable } from "uniwind";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { ScreenContainer } from "@/shared/components/ui/screen-container";
import { formatEventDateParts } from "@/shared/lib/format-date";
import { posthog } from "@/shared/lib/posthog";
import { useAuthContext } from "@/features/auth/hooks/use-auth-context";

export interface EventDetailViewProps {
  event: Event;
  participants: EventParticipantDetails[] | undefined;
  /**
   * Total participant count. Defaults to `participants.length` when omitted;
   * pass it explicitly when `participants` is only a capped preview (the public
   * page ships at most 4 rows but still shows the true total).
   */
  totalParticipantsCount?: number;
  isLoadingParticipants: boolean;
  /** Whether the current viewer has confirmed attendance. */
  isUserConfirmed: boolean;
  /** Whether the current viewer has marked interest. */
  isUserInterested: boolean;
  /** Disables the interest pill while a join/leave mutation is in flight. */
  isInterestPending: boolean;
  /** Shows a loading spinner on the confirm button. */
  isConfirmPending: boolean;
  /** Toggle interest — gated to login for logged-out viewers. */
  onToggleInterest: () => void;
  /** Confirm/cancel attendance — gated to login for logged-out viewers. */
  onPressConfirm: () => void;
  /** Open the full participants list — gated to login for logged-out viewers. */
  onPressParticipants: () => void;
  /**
   * Open the add-to-calendar sheet. The button only renders when this is
   * provided and the viewer is confirmed or interested — the public page
   * omits it entirely.
   */
  onPressAddToCalendar?: () => void;
  /** Coupon reveal outcome for a confirmed viewer — undefined until confirmed. */
  couponReveal?: CouponRevealState;
  /** Tapping a locked card while logged out — gated to login. */
  onPressLockedCoupon?: () => void;
}

/**
 * Presentational event detail screen. Holds no auth or data-fetching logic —
 * the {@link EventDetailViewProps} carry every auth-dependent value so the same
 * layout renders identically for public (logged-out) and authenticated viewers.
 */
export function EventDetailView({
  event,
  participants,
  totalParticipantsCount,
  isLoadingParticipants,
  isUserConfirmed,
  isUserInterested,
  isInterestPending,
  isConfirmPending,
  onToggleInterest,
  onPressConfirm,
  onPressParticipants,
  onPressAddToCalendar,
  couponReveal,
  onPressLockedCoupon,
}: EventDetailViewProps) {
  const insets = useSafeAreaInsets();
  const { isLoggedIn } = useAuthContext();
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [showCopied, setShowCopied] = useState(false);
  const [showShareCopied, setShowShareCopied] = useState(false);

  const participantsCount = totalParticipantsCount ?? participants?.length ?? 0;

  const mutedForeground = useCSSVariable("--color-muted-foreground") as string;
  const primary = useCSSVariable("--color-primary") as string;
  const primaryForeground = useCSSVariable(
    "--color-primary-foreground"
  ) as string;
  const foreground = useCSSVariable("--color-foreground") as string;
  const cardEvent = useCSSVariable("--color-card-event") as string;
  const eventDate = useCSSVariable("--color-event-date") as string;

  const hostHandle = useMemo(() => {
    if (!event?.hostSocialUrl) return null;
    try {
      const url = new URL(event?.hostSocialUrl);
      const handle = url.pathname.replace(/^\/+|\/+$/g, "");
      return handle ? `@${handle}` : null;
    } catch {
      return null;
    }
  }, [event?.hostSocialUrl]);

  const { datePart, timePart } = formatEventDateParts(event.dateAndTime);

  const handleOpenTicketUrl = () => {
    if (event.ticketPurchaseUrl) {
      posthog?.capture("ticket_link_clicked", { event_id: event.eventId });
      safeOpenURL(event.ticketPurchaseUrl);
    }
  };

  const handleOpenHostUrl = () => {
    if (event.hostSocialUrl) {
      safeOpenURL(event.hostSocialUrl);
    }
  };

  const handleShare = async () => {
    posthog?.capture("event_shared", {
      event_id: event.eventId,
      share_method: Platform.OS === "web" ? "clipboard" : "native_share",
    });
    if (Platform.OS === "web") {
      const url = `${window.location.origin}/event/${event.eventId}`;
      await Clipboard.setStringAsync(url);
      setShowShareCopied(true);
      setTimeout(() => setShowShareCopied(false), 2000);
    } else {
      const webUrl = `${process.env.EXPO_PUBLIC_DOMAIN_URL}/event/${event.eventId}`;
      // Make sharing message platform-agnostic
      await Share.share(
        Platform.OS === "ios"
          ? {
              title: event.title,
              message: `${event.title} — ${datePart} ${timePart}`,
              url: webUrl,
            }
          : {
              title: event.title,
              message: `${event.title} — ${datePart} ${timePart} ${webUrl}`,
            }
      );
    }
  };

  const handleCopyAddress = async () => {
    const fullAddress = `${event.address}, ${event.city}, ${event.state} - ${event.country}`;
    await Clipboard.setStringAsync(fullAddress);
    setShowCopied(true);
    setTimeout(() => setShowCopied(false), 2000);
  };

  const isCommitted = isUserConfirmed || isUserInterested;

  return (
    <>
      <Stack.Screen
        options={{
          title: event.title,
          headerShown: false,
        }}
      />
      <ScreenContainer style={{ paddingTop: 0 }}>
        <ScrollView className="flex-1" contentInsetAdjustmentBehavior="never">
          {/* Header with back and share buttons */}
          <View
            className={cn(
              "flex-row items-center px-4 pb-2",
              isLoggedIn ? "justify-between" : "justify-end"
            )}
            style={{ paddingTop: insets.top || 24 }}
          >
            {isLoggedIn && <BackButton />}
            <IconButton
              shape="circle"
              surface="tinted"
              tone="neutral"
              onPress={handleShare}
              accessibilityLabel="Share event"
            >
              <Ionicons
                name="share-social-outline"
                size={22}
                color={foreground}
              />
            </IconButton>
          </View>
          {event.imageUrl && (
            <View className="px-4 pb-2">
              <Image
                source={{ uri: resolveStorageUrl(event.imageUrl) }}
                contentFit="cover"
                style={{
                  width: "100%",
                  aspectRatio: 4 / 3,
                  borderRadius: 16,
                }}
                accessibilityLabel={`Image for ${event.title}`}
              />
            </View>
          )}
          <View className="px-5 pt-3 pb-5">
            {/* Interest Button and Count */}
            <View className="mb-6 flex-row items-center gap-3">
              <Pill
                label={isUserConfirmed ? "Confirmada" : "Tenho interesse"}
                variant={isCommitted ? "solid" : "outline"}
                tone="brand"
                selected={isCommitted}
                leftIcon={
                  <Ionicons
                    name={
                      isUserConfirmed
                        ? "checkmark-outline"
                        : isUserInterested
                          ? "heart"
                          : "heart-outline"
                    }
                    size={20}
                    color={isCommitted ? primaryForeground : primary}
                  />
                }
                disabled={isUserConfirmed || isInterestPending}
                onPress={onToggleInterest}
                accessibilityLabel={
                  isUserConfirmed
                    ? "Confirmed"
                    : isUserInterested
                      ? "Interested"
                      : "Mark as interested"
                }
              />
              <Text className="text-foreground text-sm font-semibold">
                {participantsCount} tem interesse
              </Text>
            </View>

            {/* Event Title and Subtitle */}
            <View className="mb-8">
              <Heading
                variant="display"
                accessibilityRole="header"
                className="mb-2"
              >
                {event.title}
              </Heading>
              {event.hostName && (
                <Text className="text-muted-foreground text-base">
                  {"Por "}
                  <Text className="text-event-date">{event.hostName}</Text>
                </Text>
              )}
            </View>

            {/* Event Details Card */}
            <View className="bg-card-event mb-8 rounded-lg p-5">
              {/* Date and Time */}
              <View className="mb-4 flex-row items-start gap-3">
                <Ionicons name="calendar-outline" size={20} color={primary} />
                <Text className="text-foreground flex-1 text-base">
                  <Text className="text-primary">{datePart}</Text>
                  {timePart ? ` ${timePart}` : ""}
                </Text>
              </View>
              <View className="bg-foreground/20 mb-4 h-px" />

              {/* Venue */}
              {event.hostName && (
                <>
                  <View className="mb-4 flex-row items-start gap-3">
                    <Ionicons
                      name="diamond-outline"
                      size={20}
                      color={primary}
                    />
                    <Text className="text-foreground flex-1 text-base">
                      {event.hostName}
                    </Text>
                  </View>
                  <View className="bg-foreground/20 mb-4 h-px" />
                </>
              )}

              {/* Address */}
              <View className="flex-row items-start gap-3">
                <Ionicons name="location-outline" size={20} color={primary} />
                <Text className="text-foreground flex-1 text-base">
                  {event.address}, {event.city}, {event.state} - {event.country}
                </Text>
                <View>
                  <Pressable
                    onPress={handleCopyAddress}
                    hitSlop={8}
                    accessibilityLabel="Copy address"
                    accessibilityRole="button"
                  >
                    <Ionicons name="copy-outline" size={20} color={primary} />
                  </Pressable>
                  {showCopied && (
                    <View
                      className="bg-foreground absolute top-7 right-0 items-center rounded px-2 py-1"
                      style={{ width: 80 }}
                    >
                      <Text
                        className="text-background text-xs font-medium"
                        numberOfLines={1}
                      >
                        Copiado
                      </Text>
                    </View>
                  )}
                </View>
              </View>
            </View>

            {/* Description */}
            <View className="mb-10">
              <Text
                className="text-foreground font-display mb-3 text-xl font-semibold"
                accessibilityRole="header"
              >
                Descrição
              </Text>
              <Text className="text-foreground text-sm leading-6">
                {isDescriptionExpanded
                  ? event.description
                  : event.description.length > 150
                    ? `${event.description.substring(0, 150)}...`
                    : event.description}
              </Text>
              {event.description.length > 150 && (
                <Pressable
                  onPress={() =>
                    setIsDescriptionExpanded(!isDescriptionExpanded)
                  }
                  className="mt-3 flex-row items-center justify-center gap-1"
                  accessibilityRole="button"
                  accessibilityLabel={
                    isDescriptionExpanded ? "Ler menos" : "Ler mais"
                  }
                >
                  <Text className="text-muted-foreground text-sm">
                    {isDescriptionExpanded ? "Ler menos" : "Ler mais"}
                  </Text>
                  <Ionicons
                    name={
                      isDescriptionExpanded
                        ? "chevron-up-outline"
                        : "chevron-down-outline"
                    }
                    size={16}
                    color={mutedForeground}
                  />
                </Pressable>
              )}
            </View>

            {/* Participants Preview */}
            {!isLoadingParticipants &&
              participants &&
              participants.length > 0 && (
                <View className="mb-10">
                  <Text
                    className="text-foreground font-display mb-3 text-xl font-semibold"
                    accessibilityRole="header"
                  >
                    Participantes
                  </Text>
                  <Pressable
                    onPress={onPressParticipants}
                    className="flex-row items-center active:opacity-70"
                    accessibilityRole="button"
                    accessibilityLabel="View all participants"
                  >
                    <View className="flex-row">
                      {participants.slice(0, 4).map((participant, index) => (
                        <Avatar
                          key={
                            participant.userId ?? participant.profileId ?? index
                          }
                          url={participant.profileAvatarUrl}
                          name={
                            participant.profileDisplayName ||
                            participant.profileUsername
                          }
                          size={56}
                          borderWidth={2}
                          borderColor={cardEvent}
                          style={index > 0 ? { marginLeft: -12 } : undefined}
                        />
                      ))}
                    </View>
                    {participantsCount > 4 && (
                      <Text className="text-muted-foreground ml-3 text-sm">
                        + {participantsCount - 4} participantes
                      </Text>
                    )}
                  </Pressable>
                </View>
              )}

            {event.hostName && (
              <View className="mb-10">
                <Text
                  className="text-foreground font-display mb-3 text-xl font-semibold"
                  accessibilityRole="header"
                >
                  Organizado por
                </Text>
                {event.hostSocialUrl ? (
                  <Pressable
                    onPress={handleOpenHostUrl}
                    className="flex-row items-center gap-3 active:opacity-70"
                    accessibilityRole="link"
                    accessibilityLabel={`View ${event.hostName}'s profile`}
                  >
                    {/* Avatar placeholder */}
                    <View className="bg-primary/20 h-12 w-12 items-center justify-center rounded-full">
                      <Ionicons name="person" size={24} color={primary} />
                    </View>
                    <View className="flex-1">
                      <Text className="text-foreground text-sm font-semibold">
                        {event.hostName}
                      </Text>
                      <Text className="text-muted-foreground text-xs">
                        {hostHandle ?? ""}
                      </Text>
                    </View>
                  </Pressable>
                ) : (
                  <View className="flex-row items-center gap-3">
                    {/* Avatar placeholder */}
                    <View className="bg-primary/20 h-12 w-12 items-center justify-center rounded-full">
                      <Ionicons name="person" size={24} color={primary} />
                    </View>
                    <View className="flex-1">
                      <Text className="text-foreground text-base font-semibold">
                        {event.hostName}
                      </Text>
                    </View>
                  </View>
                )}
              </View>
            )}

            {event.isPaid && (
              <View className="mb-10">
                <Text
                  className="text-foreground font-display mb-3 text-xl font-semibold"
                  accessibilityRole="header"
                >
                  Ingressos
                </Text>
                {event.ticketPurchaseUrl ? (
                  <Pressable
                    onPress={handleOpenTicketUrl}
                    className="bg-card-event flex-row items-center justify-between rounded-lg p-4 active:opacity-70"
                    accessibilityRole="link"
                    accessibilityLabel="Buy tickets for this event"
                  >
                    <View className="flex-row items-center gap-3">
                      <Ionicons
                        name="ticket-outline"
                        size={22}
                        color={eventDate}
                      />
                      <Text className="text-foreground text-base font-medium">
                        Comprar ingressos
                      </Text>
                    </View>
                    <Ionicons name="link-outline" size={22} color={eventDate} />
                  </Pressable>
                ) : (
                  <View className="bg-card-event rounded-lg p-4">
                    <View className="flex-row items-center gap-3">
                      <Ionicons
                        name="ticket-outline"
                        size={22}
                        color={primary}
                      />
                      <Text className="text-foreground text-base font-semibold">
                        Evento pago
                      </Text>
                    </View>
                  </View>
                )}
              </View>
            )}

            {event.hasCoupon && (
              <EventCouponCard
                eventId={event.eventId}
                couponLabel={event.couponLabel!}
                hasCouponCode={event.hasCouponCode}
                viewerState={getCouponViewerState(isLoggedIn, isUserConfirmed)}
                reveal={couponReveal}
                onPressLockedCoupon={onPressLockedCoupon}
              />
            )}

            {/* Tags */}
            {((event.eventTags && event.eventTags.length > 0) ||
              (event.venueTags && event.venueTags.length > 0) ||
              (event.musicTags && event.musicTags.length > 0)) && (
              <View className="mb-10">
                <Text
                  className="text-foreground font-display mb-3 text-xl font-semibold"
                  accessibilityRole="header"
                >
                  Tags
                </Text>
                <View className="flex-row flex-wrap gap-2">
                  {(event.eventTags as string[])?.map((tag) => (
                    <Tag key={tag} label={tag} />
                  ))}
                  {(event.venueTags as string[])?.map((tag) => (
                    <Tag key={tag} label={tag} />
                  ))}
                  {(event.musicTags as string[])?.map((tag) => (
                    <Tag key={tag} label={tag} />
                  ))}
                </View>
              </View>
            )}

            {/* Map */}
            {event.address && event.city && event.state && event.country && (
              <View className="mb-10">
                <Text
                  className="text-foreground font-display mb-3 text-xl font-semibold"
                  accessibilityRole="header"
                >
                  Mapa do local
                </Text>
                <EventMap
                  address={`${event.address}, ${event.city}, ${event.state} - ${event.country}`}
                />
              </View>
            )}

            <View className="mb-2 gap-3">
              <Button
                onPress={onPressConfirm}
                size="lg"
                variant={isUserConfirmed ? "destructive" : "default"}
                className={
                  isUserConfirmed ? "bg-event-date active:bg-event-date/90" : ""
                }
                loading={isConfirmPending}
                accessibilityLabel={
                  isUserConfirmed ? "Cancel attendance" : "Confirm attendance"
                }
                accessibilityHint={
                  isUserConfirmed
                    ? "Double tap to remove yourself from this event"
                    : "Double tap to confirm attendance"
                }
              >
                {isLoadingParticipants
                  ? "Loading..."
                  : isUserConfirmed
                    ? "Cancelar presença"
                    : "Confirmar presença"}
              </Button>
              {onPressAddToCalendar && isCommitted && (
                <Button
                  onPress={onPressAddToCalendar}
                  size="lg"
                  variant="outline"
                  accessibilityLabel="Adicionar ao calendário"
                >
                  <Ionicons
                    name="calendar-outline"
                    size={20}
                    color={foreground}
                  />
                  <Text className="text-foreground text-base font-semibold">
                    Adicionar ao calendário
                  </Text>
                </Button>
              )}
            </View>
          </View>
        </ScrollView>
        {showShareCopied && (
          <View
            className="bg-foreground absolute items-center rounded px-2 py-1"
            style={{ top: 96, right: 16, width: 80, zIndex: 999 }}
          >
            <Text
              className="text-background text-xs font-medium"
              numberOfLines={1}
            >
              Copiado
            </Text>
          </View>
        )}
      </ScreenContainer>
    </>
  );
}
